<template>
  <div class="container mt-4">
    <div v-if="hasAccess">
      <slot></slot>
    </div>
    <div v-else class="access-denied-wrapper">
      <div class="access-card">
        <!-- Header -->
        <div class="access-header">
          <h2 class="header-title">
            <i class="fas fa-lock header-icon" aria-hidden="true"></i>
            This page requires a subscription
          </h2>
          <p class="header-subtitle">
            How you access TAIR depends on your subscription type.
          </p>
        </div>

        <!-- Two-column layout -->
        <div class="row no-gutters access-columns">
          <!-- Left column: Individual access -->
          <div class="col-md-6">
            <div class="access-column access-column--individual">
              <h3 class="column-heading">
                <i class="fas fa-user column-icon" aria-hidden="true"></i>
                Personal subscription or complimentary access
              </h3>
              <p class="column-body">
                Log in with your linked ORCID account.
              </p>
              <div class="column-actions" v-if="!isLoggedIn">
                <button
                  @click="redirectToLogin"
                  class="btn btn-primary btn-block mb-2"
                >
                  Log In
                </button>
                <button
                  @click="redirectToRegister"
                  class="btn btn-outline-secondary btn-block"
                >
                  Register
                </button>
              </div>
              <div class="column-actions" v-if="isLoggedIn && !hasOrcidConnected">
                <button
                  @click="connectOrcid"
                  class="btn btn-outline-secondary btn-block"
                >
                  Connect ORCID
                </button>
              </div>
            </div>
          </div>

          <!-- Right column: Institutional access -->
          <div class="col-md-6">
            <div class="access-column access-column--institutional">
              <h3 class="column-heading">
                <i class="fas fa-university column-icon" aria-hidden="true"></i>
                Institutional subscription
              </h3>
              <p class="column-body">
                Your IP address is not on your institution's approved list.
                Connect through your library proxy server.
              </p>
              <div class="column-actions">
                <a
                  href="https://ui.arabidopsis.org/#/contentaccess/guide?partnerId=tair"
                  class="btn btn-outline-primary btn-block"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Troubleshooting guide
                  <i class="fas fa-external-link-alt ml-1 small" aria-hidden="true"></i>
                </a>
              </div>

              <!-- Chinese guidance nested inside institutional column -->
              <div lang="zh" class="chinese-inline">
                <p class="chinese-label">机构访问遇到问题？</p>
                <p class="chinese-body">
                  您的IP地址不在贵单位的备案范围内。请通过贵单位的图书馆代理服务器连接。
                </p>
                <a
                  href="https://ui.arabidopsis.org/#/contentaccess/guide?partnerId=tair"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="chinese-link"
                >
                  故障排查指南
                  <i class="fas fa-external-link-alt ml-1" aria-hidden="true"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Compact utility footer -->
        <div class="access-footer">
          <span v-if="clientIp">Your IP: <strong>{{ clientIp }}</strong></span>
          <span class="footer-divider" v-if="clientIp" aria-hidden="true"></span>
          <a href="mailto:info@phoenixbioinformatics.org">Contact us</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import axios from "axios";

export default {
  name: "AccessControl",
  data() {
    return {
      clientIp: null,
    };
  },
  computed: {
    ...mapGetters(["userStatus"]),
    isLoggedIn() {
      return this.userStatus.isLoggedIn;
    },
    hasOrcidConnected() {
      return !!this.userStatus.orcidId;
    },
    hasSubscription() {
      return this.userStatus.subscriptionStatus === "active";
    },
    hasAccess() {
      return (
        (this.isLoggedIn && this.hasOrcidConnected) || this.hasSubscription
      );
    },
  },
  async created() {
    try {
      const response = await axios.get("https://api.ipify.org?format=json");
      this.clientIp = response.data.ip;
    } catch {
      this.clientIp = null;
    }
  },
  methods: {
    redirectToLogin() {
      this.$router.push("/login");
    },
    redirectToRegister() {
      this.$router.push("/register");
    },
    connectOrcid() {
      this.$router.push("/linktoorcid");
    },
  },
};
</script>

<style scoped>
.access-denied-wrapper {
  max-width: 880px;
  margin: 0 auto;
}

.access-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

/* ── Header ── */
.access-header {
  text-align: center;
  padding: 28px 32px 22px;
  border-bottom: 1px solid #e9ecef;
}

.header-icon {
  color: #1a56db;
  margin-right: 8px;
  font-size: 1.1rem;
}

.header-title {
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0;
  color: #1a1a2e;
  line-height: 1.3;
}

.header-subtitle {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 6px 0 0;
}

/* ── Columns ── */
.access-columns {
  display: flex;
}

.access-column {
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  height: 100%;
}

.access-column--individual {
  border-right: 1px solid #e9ecef;
}

.column-icon {
  color: #6b7280;
  margin-right: 6px;
  font-size: 0.9rem;
}

.column-heading {
  font-size: 1.05rem;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px;
  line-height: 1.4;
}

.column-body {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0 0 16px;
  line-height: 1.55;
}

.column-actions .btn {
  font-size: 0.9rem;
  padding: 10px 18px;
  min-height: 42px;
}

/* ── Chinese section inside institutional column ── */
.chinese-inline {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px dashed #d1d5db;
}

.chinese-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 4px;
}

.chinese-body {
  font-size: 0.82rem;
  color: #6b7280;
  margin: 0 0 6px;
  line-height: 1.55;
}

.chinese-link {
  font-size: 0.82rem;
  color: #1a56db;
  text-decoration: underline;
}

/* ── Footer ── */
.access-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 14px 32px;
  background: #f8f9fb;
  border-top: 1px solid #e9ecef;
  font-size: 0.85rem;
  color: #6b7280;
}

.footer-divider {
  width: 1px;
  height: 14px;
  background: #d1d5db;
}

.access-footer a {
  color: #1a56db;
  text-decoration: underline;
}

/* ── WCAG ── */
a:focus,
button:focus {
  outline: 2px solid #1a56db;
  outline-offset: 2px;
}

.btn {
  min-height: 42px;
  min-width: 44px;
}

/* ── Responsive: stack on mobile ── */
@media (max-width: 767px) {
  .access-header {
    padding: 20px 24px 16px;
  }

  .access-column {
    padding: 20px 24px;
  }

  .access-column--individual {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
  }

  .access-footer {
    padding: 12px 24px;
  }
}
</style>
